/**
 * Smart lottery generator with opt-in filters.
 * All randomness is uniform; filters are constraints applied via rejection sampling.
 */

export interface NumberStat {
  number: number;
  frequency: number;
  delay: number | null;
}

export interface SmartFilters {
  parityBalance: boolean;        // pares/ímpares equilibrados
  sumInRange: boolean;            // soma na faixa típica
  decadeSpread: boolean;          // distribuição por décadas
  noLongRuns: boolean;            // sem sequências longas (>3 consecutivas)
  preferHot: boolean;             // priorizar dezenas quentes
  includeOverdue: boolean;        // incluir dezenas atrasadas
  preferHotPairs: boolean;        // priorizar duplas (pares de dezenas) mais frequentes
  includeOverduePairs: boolean;   // priorizar duplas mais atrasadas
  avoidPastDraws: boolean;        // evitar combinações já sorteadas
  useFixed: boolean;              // usar fixas/excluídas
  fixedNumbers: number[];
  excludedNumbers: number[];
}

export const DEFAULT_FILTERS: SmartFilters = {
  parityBalance: false,
  sumInRange: false,
  decadeSpread: false,
  noLongRuns: false,
  preferHot: false,
  includeOverdue: false,
  preferHotPairs: false,
  includeOverduePairs: false,
  avoidPastDraws: false,
  useFixed: false,
  fixedNumbers: [],
  excludedNumbers: [],
};

export interface PairStat {
  /** Co-occurrence count across analyzed draws. */
  frequency: number;
  /** Number of draws since the pair last appeared together (0 = last draw). */
  delay: number;
}

export type PairStats = Map<string, PairStat>;

const pairKey = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);

/** Build co-occurrence stats from historical draws. `pastDraws` is ordered most-recent-first. */
export function computePairStats(pastDraws: number[][]): PairStats {
  const map: PairStats = new Map();
  pastDraws.forEach((draw, idx) => {
    for (let i = 0; i < draw.length; i++) {
      for (let j = i + 1; j < draw.length; j++) {
        const k = pairKey(draw[i], draw[j]);
        const cur = map.get(k);
        if (cur) cur.frequency += 1;
        else map.set(k, { frequency: 1, delay: idx });
      }
    }
  });
  return map;
}

export interface GenerateInput {
  min: number;
  max: number;
  pick: number;
  filters: SmartFilters;
  stats: NumberStat[];
  pastDraws: number[][];          // arrays of sorted drawn numbers
  sumRange: { min: number; max: number } | null;
  count: number;                  // how many games to generate
  maxAttemptsPerGame?: number;
  pairStats?: PairStats;
}

export interface GenerateResult {
  games: number[][];
  attempts: number;
  rejected: number;
}

/** Build a weighted pool of candidate numbers based on filter preferences. */
function buildPool(input: GenerateInput): { pool: number[]; weights: number[] } {
  const { min, max, filters, stats } = input;
  const excluded = new Set(filters.useFixed ? filters.excludedNumbers : []);
  const pool: number[] = [];
  const weights: number[] = [];
  const statMap = new Map(stats.map((s) => [s.number, s]));

  // Frequency-based weight scaling
  const freqs = stats.map((s) => s.frequency);
  const maxFreq = Math.max(1, ...freqs);
  const delays = stats.map((s) => s.delay ?? 0);
  const maxDelay = Math.max(1, ...delays);

  for (let n = min; n <= max; n++) {
    if (excluded.has(n)) continue;
    let w = 1;
    const s = statMap.get(n);
    if (filters.preferHot && s) {
      w *= 1 + (s.frequency / maxFreq) * 1.5; // hot numbers up to 2.5x weight
    }
    if (filters.includeOverdue && s && s.delay != null) {
      w *= 1 + (s.delay / maxDelay) * 1.5;
    }
    pool.push(n);
    weights.push(w);
  }
  return { pool, weights };
}

function weightedSample(
  pool: number[],
  weights: number[],
  k: number,
  fixed: number[],
  pairBoost?: (candidate: number, picked: number[]) => number,
): number[] {
  const picked = new Set(fixed);
  const remaining = pool.map((n, i) => ({ n, w: weights[i] })).filter((x) => !picked.has(x.n));
  while (picked.size < k && remaining.length) {
    const pickedArr = pairBoost ? [...picked] : [];
    const adjusted = pairBoost
      ? remaining.map((x) => x.w * pairBoost(x.n, pickedArr))
      : remaining.map((x) => x.w);
    const total = adjusted.reduce((acc, w) => acc + w, 0);
    if (total <= 0) break;
    let r = Math.random() * total;
    let idx = 0;
    for (let i = 0; i < adjusted.length; i++) {
      r -= adjusted[i];
      if (r <= 0) {
        idx = i;
        break;
      }
    }
    picked.add(remaining[idx].n);
    remaining.splice(idx, 1);
  }
  return [...picked].sort((a, b) => a - b);
}

/** Validation pass — applies non-weighted filters as constraints. */
function validate(game: number[], input: GenerateInput, pastSet: Set<string>): boolean {
  const { filters, sumRange, min, max } = input;

  if (filters.parityBalance) {
    const evens = game.filter((n) => n % 2 === 0).length;
    const odds = game.length - evens;
    // allow imbalance of at most 2
    if (Math.abs(evens - odds) > 2) return false;
  }

  if (filters.sumInRange && sumRange) {
    const s = game.reduce((a, b) => a + b, 0);
    if (s < sumRange.min || s > sumRange.max) return false;
  }

  if (filters.decadeSpread) {
    // bucket by decade. require at least 3 distinct decades touched.
    const decades = new Set(game.map((n) => Math.floor((n - min) / 10)));
    const totalDecades = Math.ceil((max - min + 1) / 10);
    const required = Math.min(3, totalDecades);
    if (decades.size < required) return false;
  }

  if (filters.noLongRuns) {
    let run = 1;
    for (let i = 1; i < game.length; i++) {
      if (game[i] === game[i - 1] + 1) {
        run++;
        if (run > 3) return false;
      } else {
        run = 1;
      }
    }
  }

  if (filters.avoidPastDraws) {
    if (pastSet.has(game.join(","))) return false;
  }

  return true;
}

export function generateSmart(input: GenerateInput): GenerateResult {
  const { count, filters, pick } = input;
  const maxAttempts = input.maxAttemptsPerGame ?? 500;
  const pastSet = new Set(input.pastDraws.map((d) => d.join(",")));
  const fixed = filters.useFixed
    ? filters.fixedNumbers.filter((n) => n >= input.min && n <= input.max).slice(0, pick)
    : [];

  const { pool, weights } = buildPool(input);

  // Precompute normalization for pair-based boosting
  let pairBoost: ((candidate: number, picked: number[]) => number) | undefined;
  if ((filters.preferHotPairs || filters.includeOverduePairs) && input.pairStats && input.pairStats.size) {
    let maxFreq = 1;
    let maxDelay = 1;
    input.pairStats.forEach((s) => {
      if (s.frequency > maxFreq) maxFreq = s.frequency;
      if (s.delay > maxDelay) maxDelay = s.delay;
    });
    const stats = input.pairStats;
    pairBoost = (candidate, picked) => {
      if (!picked.length) return 1;
      let sum = 0;
      for (const p of picked) {
        const key = candidate < p ? `${candidate}-${p}` : `${p}-${candidate}`;
        const s = stats.get(key);
        if (!s) continue;
        if (filters.preferHotPairs) sum += s.frequency / maxFreq;
        if (filters.includeOverduePairs) sum += s.delay / maxDelay;
      }
      return 1 + (sum / picked.length) * 1.5;
    };
  }

  const games: number[][] = [];
  let attempts = 0;
  let rejected = 0;
  const seen = new Set<string>();

  for (let g = 0; g < count; g++) {
    let found = false;
    for (let a = 0; a < maxAttempts; a++) {
      attempts++;
      const candidate = weightedSample(pool, weights, pick, fixed, pairBoost);
      if (candidate.length !== pick) {
        rejected++;
        continue;
      }
      const key = candidate.join(",");
      if (seen.has(key)) {
        rejected++;
        continue;
      }
      if (!validate(candidate, input, pastSet)) {
        rejected++;
        continue;
      }
      seen.add(key);
      games.push(candidate);
      found = true;
      break;
    }
    if (!found) break; // give up — filters too restrictive
  }

  return { games, attempts, rejected };
}

/** Compute typical sum range from past draws (median ± 1 stddev). */
export function computeSumRange(pastDraws: number[][]): { min: number; max: number } | null {
  if (!pastDraws.length) return null;
  const sums = pastDraws.map((d) => d.reduce((a, b) => a + b, 0));
  const mean = sums.reduce((a, b) => a + b, 0) / sums.length;
  const variance = sums.reduce((acc, s) => acc + (s - mean) ** 2, 0) / sums.length;
  const sd = Math.sqrt(variance);
  return { min: Math.round(mean - sd), max: Math.round(mean + sd) };
}